<template>
  <div>
    <!-- Hero -->
    <section class="compare-hero">
      <v-container class="py-12 py-md-16">
        <div class="text-center mb-8">
          <v-chip variant="tonal" color="primary" class="mb-4">Comparison</v-chip>
          <h1 class="compare-h1 mb-4">Song7: an alternative to Planning Center Music Stand</h1>
          <p class="text-h6 text-medium-emphasis mx-auto" style="max-width: 820px">
            Music Stand is a paid add-on to Planning Center Services that lets musicians read, annotate, and turn pages
            on iPad or Android. Song7 is a standalone web-and-mobile worship platform that manages your songs, chord
            charts, setlists, and live presentation in a single tool, with no separate subscription required.
          </p>
          <p class="text-caption text-medium-emphasis mt-4">
            Last updated June 2026 · Honest comparison · Updated quarterly
          </p>

          <div class="mt-6">
            <a href="#migrate" class="switcher-callout">
              <v-icon icon="$check" size="18" color="success" class="mr-1" />
              Already on Planning Center? See how to switch to Song7
              <v-icon icon="$arrowRight" size="16" class="ml-1" />
            </a>
          </div>
        </div>

        <v-row class="mt-4" justify="center">
          <v-col cols="12" md="5">
            <CompareHighlightCard>
              <div class="d-flex align-center mb-4">
                <v-icon icon="$musicCircleOutline" color="" size="32" class="mr-3" />
                <strong class="text-h6 font-weight-bold">Song7</strong>
              </div>
              <p class="text-body-2 mb-3">
                All-in-one worship platform with chord charts, setlists, transposition, team sync, and presentation.
                Web-first, works on any device with a browser.
              </p>
              <ul class="compare-quick-facts">
                <li><strong>Platforms:</strong> Any browser; installable as a PWA</li>
                <li><strong>Requires:</strong> Nothing else</li>
                <li>
                  <strong>Pricing:</strong> Free for teams up to {{ FREE_PLAN.members }} members; paid plans beyond that
                </li>
              </ul>
              <v-btn
                :to="appPath('spaces')"
                color="primary"
                variant="flat"
                rounded="pill"
                class="mt-4"
                append-icon="$arrowRight"
              >
                Try Song7 free
              </v-btn>
            </CompareHighlightCard>
          </v-col>

          <v-col cols="12" md="5">
            <v-card class="pa-6 h-100" elevation="0" border rounded="xl">
              <div class="d-flex align-center mb-4">
                <!-- <v-icon icon="$musicNote" color="grey" size="32" class="mr-3" /> -->
                <strong class="text-h6 font-weight-bold">Music Stand by Planning Center</strong>
              </div>
              <p class="text-body-2 mb-3">
                Dedicated chord chart display app for musicians on stage. Reads songs from Planning Center Services,
                supports annotations and foot-pedal page turns.
              </p>
              <ul class="compare-quick-facts">
                <li><strong>Platforms:</strong> iOS, Android (no Windows)</li>
                <li><strong>Requires:</strong> Planning Center Services subscription</li>
                <li><strong>Pricing:</strong> $5–$10/month add-on, plus Services</li>
              </ul>
              <v-btn
                href="https://www.planningcenter.com/music-stand"
                target="_blank"
                rel="noopener nofollow"
                variant="outlined"
                rounded="pill"
                class="mt-4"
                append-icon="$arrowRight"
              >
                View Music Stand
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Quick verdict -->
    <section class="bg-surface-bright">
      <v-container class="py-12 py-md-16">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">Quick verdict</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center">
          The short answer for the people who just want to know.
        </p>
        <v-row>
          <v-col cols="12" md="6">
            <v-card class="pa-6 h-100" elevation="0" border rounded="xl">
              <h3 class="text-h6 font-weight-bold mb-3">Choose Music Stand if&hellip;</h3>
              <ul class="verdict-list">
                <li>Your church already uses Planning Center Services for scheduling and song management.</li>
                <li>
                  Your musicians are exclusively on iPad or Android tablets and you want a dedicated stage-reading app.
                </li>
                <li>
                  You need hardware foot-pedal page turns today (AirTurn DUO 500 / BT500S-2 are the recommended pedals).
                </li>
              </ul>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <CompareHighlightCard>
              <h3 class="text-h6 font-weight-bold mb-3">Choose Song7 if&hellip;</h3>
              <ul class="verdict-list">
                <li>
                  You want one tool instead of two: song library, setlists, transposition, and presentation in a single
                  product.
                </li>
                <li>
                  Your team is mixed: laptops, phones, tablets, Mac, Windows. Music Stand can't run on Windows; Song7
                  runs in any browser.
                </li>
                <li>
                  You're not already in the Planning Center ecosystem and don't want to commit to two separate
                  subscriptions.
                </li>
              </ul>
            </CompareHighlightCard>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- At-a-glance comparison table -->
    <section>
      <v-container class="py-12 py-md-16">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">At-a-glance comparison</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center mx-auto" style="max-width: 720px">
          Feature-by-feature snapshot of how Song7 and Music Stand by Planning Center line up.
        </p>

        <v-card class="overflow-hidden" elevation="0" border rounded="xl">
          <v-table density="comfortable" class="compare-table">
            <thead>
              <tr>
                <th scope="col">Feature</th>
                <th scope="col" class="text-center">Song7</th>
                <th scope="col" class="text-center">Music Stand (Planning Center)</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="row in matrix" :key="row.feature">
                <tr v-if="row.group" class="compare-table-group">
                  <th scope="rowgroup" colspan="3" class="text-uppercase text-caption font-weight-bold">
                    {{ row.group }}
                  </th>
                </tr>
                <tr>
                  <td class="font-weight-medium">{{ row.feature }}</td>
                  <td class="text-center">
                    <v-icon v-if="row.song7 === true" icon="$check" color="success" aria-label="Yes" />
                    <v-icon v-else-if="row.song7 === false" icon="$close" color="grey" aria-label="No" />
                    <span v-else class="text-caption">{{ row.song7 }}</span>
                  </td>
                  <td class="text-center">
                    <v-icon v-if="row.musicStand === true" icon="$check" color="success" aria-label="Yes" />
                    <v-icon v-else-if="row.musicStand === false" icon="$close" color="grey" aria-label="No" />
                    <span v-else class="text-caption">{{ row.musicStand }}</span>
                  </td>
                </tr>
              </template>
            </tbody>
          </v-table>
        </v-card>
        <p class="text-caption text-medium-emphasis mt-3 text-center">
          Sources: planningcenter.com/music-stand, planningcenter.com/services. Verified June 2026.
        </p>

        <div class="text-center mt-8">
          <v-btn
            :to="appPath('spaces')"
            color="primary"
            variant="flat"
            size="large"
            rounded="pill"
            append-icon="$arrowRight"
          >
            Start your free Song7 space
          </v-btn>
          <p class="text-caption text-medium-emphasis mt-2">No credit card required</p>
        </div>
      </v-container>
    </section>

    <!-- What is Music Stand -->
    <section class="bg-surface-bright">
      <v-container class="py-12 py-md-16">
        <v-row>
          <v-col cols="12" md="6">
            <h2 class="text-h4 font-weight-bold mb-4">What is Music Stand by Planning Center?</h2>
            <p class="text-body-1 mb-4">
              Music Stand is a mobile chord chart display app from Planning Center, the company behind the Planning
              Center Services platform for church scheduling and worship planning.
            </p>
            <p class="text-body-1 mb-4">
              Music Stand isn't a standalone product. It's an add-on that pairs with Planning Center Services. Songs and
              charts are managed inside Services, then read on stage through Music Stand on iPad, iPhone, or Android
              tablet. Without Services, Music Stand has nothing to show.
            </p>
            <p class="text-body-1 mb-0">
              Its job is narrow and well-defined: display sheet music or chord charts during a service, let musicians
              annotate, and turn pages by swipe, scroll, or foot pedal. Page sync between linked devices, an audio
              player with section looping, a visual metronome, and external-display mirroring round out the feature set.
            </p>
          </v-col>
          <v-col cols="12" md="6">
            <h2 class="text-h4 font-weight-bold mb-4">What is Song7?</h2>
            <p class="text-body-1 mb-4">
              Song7 is a standalone worship music platform built for the whole workflow: managing your song library,
              building setlists for Sunday services, transposing on the fly, syncing the team in real time, and running
              the presentation. One tool, one bill.
            </p>
            <p class="text-body-1 mb-4">
              It runs in any modern browser and installs as a Progressive Web App on iOS, Android, and desktop,
              including Windows, which Music Stand explicitly does not support. No app store gatekeeping; team members
              get access directly in their browser.
            </p>
            <p class="text-body-1 mb-0">
              Song7 doesn't require Planning Center Services or any other product underneath it. If you're starting
              fresh, or you want to leave the Planning Center ecosystem entirely, Song7 replaces both halves (the
              management side and the on-stage display side) with a single subscription.
            </p>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Feature deep dive -->
    <section>
      <v-container class="py-12 py-md-16" style="max-width: 920px">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">Feature-by-feature deep dive</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center">
          Where each product wins, where each one falls short.
        </p>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Song and chord chart management</h3>
          <p class="mb-3">
            <strong>Music Stand:</strong> Doesn't manage songs itself. Charts live in Planning Center Services, which
            integrates with MultiTracks, SongSelect, and PraiseCharts as primary sources. You add a chart to Services
            first, then it appears in Music Stand on your devices.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Your own song library and chord chart management. Import from ChordPro files, paste
            in plain text with chord brackets, snap a photo of a printed chord sheet, or build charts from scratch.
            Charts are organized in Spaces (multi-tenant for churches with multiple campuses or teams), searchable, and
            shared with the whole team automatically.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Setlist creation and planning</h3>
          <p class="mb-3">
            <strong>Music Stand:</strong> Pulls setlists built in Planning Center Services. You won't build a Sunday
            setlist inside Music Stand itself. That happens in Services on the web. Music Stand is the read-side of the
            workflow.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Drag-and-drop setlist builder is a core part of the app. Pick songs from your
            library, reorder by dragging, set the key per song, publish the set, and the whole team sees it instantly.
            No separate planning product to log into.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Transposition and key changes</h3>
          <p class="mb-3">
            <strong>Music Stand:</strong> Supports transposed chord charts, but the transposition itself depends on how
            the chart was prepared upstream in Services or in the chart source (MultiTracks, PraiseCharts). The Music
            Stand app reads what Services serves it.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> One-tap transposition into any key directly in the app. Change a song's key for a
            Sunday morning and every musician's chart updates in real time. No re-importing, no managing separate copies
            per key.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Live presentation</h3>
          <p class="mb-3">
            <strong>Music Stand:</strong> Designed for musicians reading on stage. External display mirroring sends the
            chart to a TV or monitor so the band can read it on a bigger screen, with multi-device sync keeping linked
            devices on the same page. It's not built to project lyrics for the congregation. That's a separate job for a
            different tool.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Has a congregation-facing presentation mode that displays clean, full-screen lyrics
            on any screen, with no extra hardware needed. That means Song7 covers both the band-reading job and the
            lyrics-projection job in one tool, where Music Stand only covers the band-reading half.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Annotations and sticky notes</h3>
          <p class="mb-3">
            <strong>Music Stand:</strong> Mature annotation tools: drawing, highlighting, and notes layered on top of
            charts, with sync across linked devices. This is one of Music Stand's stronger areas.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Each song has a notes section the whole team sees. Heavy free-form
            drawing/annotation on top of a chart is not currently the focus. Song7's chord-chart model favors structured
            editing of the chart itself over overlay markup.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Foot-pedal page turns</h3>
          <p class="mb-3">
            <strong>Music Stand:</strong> Yes, with hardware recommendations: AirTurn DUO 500 and AirTurn BT500S-2. For
            musicians playing with both hands and needing hands-free page turns, this is one of Music Stand's most
            concrete advantages.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Not currently a first-class feature. Browser-based pages can accept keyboard input
            from a Bluetooth pedal mapped to arrow keys, but Song7 doesn't ship a curated pedal integration the way
            Music Stand does. If hands-free page turns are critical, this is a category where Music Stand still wins
            today.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Platform support</h3>
          <p class="mb-3">
            <strong>Music Stand:</strong> iOS (iPhone and iPad) and Android. Planning Center states explicitly that
            "Music Stand isn't compatible with Windows devices." If anyone on your team uses a Windows laptop on stage,
            Music Stand is a non-starter for them.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Any modern browser, desktop or mobile. Installable as a Progressive Web App on iOS,
            Android, macOS, ChromeOS, and Windows. No platform exclusions.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Team collaboration</h3>
          <p class="mb-3">
            <strong>Music Stand:</strong> Collaboration happens primarily through Planning Center Services: scheduling
            volunteers, assigning songs, sending plans. Music Stand itself is more of a personal reading app per
            musician.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Team collaboration is built into the same product. Multiple editors can manage songs
            and setlists, members see published sets in real time, and everything stays in sync without a separate
            planning tool.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Offline access</h3>
          <p class="mb-3">
            <strong>Music Stand:</strong> Charts are cached on-device for offline reading on stage, which matters when
            the church Wi-Fi gives out mid-service. This is a real strength of a native mobile app.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> As a PWA, Song7 can cache content for offline reading, though the offline experience
            is currently lighter than a fully native app. For most services the connection holds; for situations where
            offline is non-negotiable, Music Stand's native caching has the edge.
          </p>
        </article>
      </v-container>
    </section>

    <!-- Product showcase: where Song7 wins -->
    <section>
      <v-container class="py-12 py-md-16">
        <div class="text-center mb-10">
          <p class="text-primary font-weight-bold mb-2">Beyond the comparison</p>
          <h2 class="text-h4 font-weight-bold mb-3">Things Song7 does that Music Stand can't</h2>
          <p class="text-h6 text-medium-emphasis mx-auto" style="max-width: 720px">
            The six features churches switching from Planning Center mention most.
          </p>
        </div>

        <v-row>
          <v-col v-for="card in showcase" :key="card.title" cols="12" sm="6" lg="4">
            <v-card class="pa-6 h-100 showcase-card" elevation="0" border rounded="xl">
              <div class="showcase-icon mb-4">
                <v-icon :icon="card.icon" size="24" />
              </div>
              <h3 class="text-h6 font-weight-bold mb-2">{{ card.title }}</h3>
              <p class="text-body-2 mb-0">{{ card.text }}</p>
            </v-card>
          </v-col>
        </v-row>

        <div class="text-center mt-10">
          <v-btn to="/" color="primary" variant="text" rounded="pill" append-icon="$arrowRight">
            See the full Song7 feature tour
          </v-btn>
        </div>
      </v-container>
    </section>

    <!-- Pricing comparison -->
    <section class="bg-surface-bright">
      <v-container class="py-12 py-md-16" style="max-width: 920px">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">Pricing comparison</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center">What you actually pay each month, all-in.</p>

        <v-row>
          <v-col cols="12" md="6">
            <v-card class="pa-6 h-100" elevation="0" border rounded="xl">
              <h3 class="text-h6 font-weight-bold mb-3">Music Stand total cost</h3>
              <p class="text-body-2 mb-3">
                Music Stand is sold as an add-on to Planning Center Services. You can't subscribe to Music Stand by
                itself. Services is the parent product, and both are billed separately.
              </p>
              <ul class="price-breakdown">
                <li>
                  <strong>Services free tier:</strong> $0 (5 team members, 100&nbsp;MB storage). Music Stand add-on
                  pricing on the free tier isn't publicly stated by Planning Center, so check their pricing page for the
                  latest.
                </li>
                <li><strong>Up to 20 team members:</strong> $15/mo Services + Music Stand add-on</li>
                <li>
                  <strong>Up to 50 team members:</strong> $32/mo Services + $5/mo Music Stand =
                  <strong>$37/mo</strong>
                </li>
                <li>
                  <strong>Up to 150 team members:</strong> $69/mo Services + $10/mo Music Stand =
                  <strong>$79/mo</strong>
                </li>
                <li>
                  <strong>Larger churches (400+ team members):</strong> $115/mo+ for Services plus a Music Stand add-on;
                  current add-on rate listed on planningcenter.com/music-stand.
                </li>
              </ul>
              <p class="text-caption mt-3 mb-0">
                Source: planningcenter.com pricing pages, verified June 2026. Numbers may change.
              </p>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <CompareHighlightCard>
              <h3 class="text-h6 font-weight-bold mb-3">Song7 total cost</h3>
              <p class="text-body-2 mb-3">
                Song7 is a single product. The price you pay is the all-in price. There's no separate planning
                subscription required underneath it.
              </p>
              <p class="text-body-2 mb-4">
                Song7 is free for teams of up to {{ FREE_PLAN.members }} members, with every feature included and no
                credit card required. Paid plans for bigger teams come with a 30-day trial.
              </p>
              <v-btn to="/pricing" color="primary" variant="flat" rounded="pill" append-icon="$arrowRight">
                See Song7 pricing
              </v-btn>
              <p class="text-caption mt-4 mb-0">One subscription. One product. No add-ons required.</p>
            </CompareHighlightCard>
          </v-col>
        </v-row>

        <v-card class="mt-8 pa-6" elevation="0" color="warning" variant="tonal" rounded="xl">
          <h3 class="text-h6 font-weight-bold mb-2">Where Planning Center pricing gets surprising</h3>
          <p class="text-body-2 mb-0">
            To use Music Stand at all, you need a Planning Center Services subscription ($15–$69/mo+) <em>and</em> the
            Music Stand add-on ($5–$10/mo) on top: two products, two bills. In Song7, the full feature set
            (transposition, multi-team Spaces, real-time team sync, presentation mode) is part of a single subscription,
            including on the free plan. If you already pay for Services for scheduling, the Music Stand add-on is
            genuinely cheap incremental cost; if you don't, you're committing to two products to get what Song7 ships in
            one.
          </p>
        </v-card>

        <v-alert type="info" variant="tonal" class="mt-4" density="comfortable" icon="$informationOutline">
          <strong>Honest note on the comparison:</strong> If your church already pays for Planning Center Services for
          scheduling (which many churches do, and which Song7 doesn't replace 1:1), then Music Stand's $5–$10/mo add-on
          is genuinely cheap incremental cost. The Song7 case is strongest when you don't already have Services, when
          you'd rather not run two tools, or when you want one bill instead of two.
        </v-alert>

        <div class="text-center mt-8">
          <v-btn to="/pricing" color="primary" variant="outlined" size="large" rounded="pill" append-icon="$arrowRight">
            See Song7 pricing in full
          </v-btn>
        </div>
      </v-container>
    </section>

    <!-- Migration -->
    <section id="migrate">
      <v-container class="py-12 py-md-16" style="max-width: 920px">
        <h2 class="text-h4 font-weight-bold mb-4">Migrating from Music Stand to Song7</h2>
        <p class="text-body-1 mb-4">Switching from Planning Center Music Stand to Song7. The practical steps:</p>
        <ol class="migration-steps">
          <li>
            <strong>Get your song library out of Planning Center Services.</strong> Save your charts in a portable
            format Song7 accepts: ChordPro, plain text format with chords. ChordPro preserves chord positions and parses
            cleanly. For charts you can only get as a printed sheet or image, Song7's photo-to-chart capture is the
            fallback path.
          </li>
          <li>
            <strong>Create a Song7 account and a Space for your church.</strong> Free for teams of up to
            {{ FREE_PLAN.members }} members, no credit card required.
          </li>
          <li>
            <strong>Import your songs into Song7.</strong> Paste in ChordPro or text with chord brackets; Song7 parses
            the chord positions and renders the chart.
          </li>
          <li>
            <strong>Invite your team.</strong> Members get instant access in their browser. No app store downloads, no
            per-user provisioning friction.
          </li>
          <li>
            <strong>Create one upcoming setlist to test the workflow</strong> before fully switching. Run a midweek
            rehearsal on Song7 to surface anything specific to your team's habits.
          </li>
        </ol>
        <p class="text-body-1 mt-4 mb-0">
          If you only want to leave Music Stand but stay on Planning Center Services for scheduling, Song7 isn't the
          right swap for you. Song7 is designed to replace the whole stack, not just the display piece. In that case,
          Music Stand is doing the right job.
        </p>
      </v-container>
    </section>

    <!-- Decision framework -->
    <section class="bg-surface-bright">
      <v-container class="py-12 py-md-16" style="max-width: 920px">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">Which one should you pick?</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center">A short decision guide based on your situation.</p>

        <v-row>
          <v-col cols="12" md="6">
            <v-card class="pa-6 h-100" elevation="0" border rounded="xl">
              <h3 class="text-h6 font-weight-bold mb-3">Stay with (or pick) Music Stand if</h3>
              <ul class="verdict-list">
                <li>You already use Planning Center Services for volunteer scheduling.</li>
                <li>Your musicians are iPad-only and the iPad experience is non-negotiable.</li>
                <li>Hardware foot-pedal page turns are a deal-breaker.</li>
                <li>Heavy chart annotation is a core part of how your team rehearses.</li>
                <li>You need rock-solid native offline mode on stage.</li>
              </ul>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <CompareHighlightCard>
              <h3 class="text-h6 font-weight-bold mb-3">Switch to (or pick) Song7 if</h3>
              <ul class="verdict-list">
                <li>You want one tool, one bill, one place to manage everything.</li>
                <li>Your team mixes laptops (including Windows), phones, and tablets.</li>
                <li>You're not currently locked into Planning Center Services.</li>
                <li>You want to onboard volunteers without app-store friction.</li>
                <li>
                  Drag-and-drop setlist building and one-tap transposition are higher priority than pedal-driven page
                  turns.
                </li>
              </ul>
            </CompareHighlightCard>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Social proof -->
    <section class="bg-surface-bright">
      <ClientOnly>
        <FeaturedFeedback class="py-8 py-md-12" />
      </ClientOnly>
    </section>

    <!-- FAQ -->
    <section>
      <v-container class="py-12 py-md-16" style="max-width: 920px">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">Frequently asked questions</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center">
          The questions worship leaders actually ask before switching.
        </p>

        <FaqAccordion :groups="[{ items: faqItems }]" />
      </v-container>
    </section>

    <!-- Was this comparison helpful -->
    <section>
      <v-container class="py-4" style="max-width: 720px">
        <PageFeedback
          title="Planning Center Music Stand"
          context="Comparison"
          question="Was this comparison helpful?"
        />
      </v-container>
    </section>

    <!-- Final CTA -->
    <section>
      <v-container class="py-12 py-md-16 text-center" style="max-width: 720px">
        <h2 class="text-h4 font-weight-bold mb-4">Ready to try Song7?</h2>
        <p class="text-h6 text-medium-emphasis mb-6">
          Free for teams of up to {{ FREE_PLAN.members }} members, no credit card required. Bring your team across
          whenever you're ready.
        </p>
        <v-btn
          :to="appPath('spaces')"
          color="primary"
          variant="flat"
          size="x-large"
          rounded="pill"
          append-icon="$arrowRight"
          class="pulse-button"
        >
          Start free
        </v-btn>
        <p class="text-caption text-medium-emphasis mt-4">
          Questions? <NuxtLink to="/contact">Talk to us first</NuxtLink>.
        </p>
      </v-container>
    </section>

    <!-- Related comparisons -->
    <CompareRelated current-slug="planning-center-music-stand" />
  </div>
</template>

<script setup>
import { useSeo } from '~/composables/useSeo'
import { FREE_PLAN } from '@@/constants/publicPlans'

definePageMeta({
  layout: 'website',
})

const showcase = [
  {
    icon: '$presentation',
    title: 'Congregation lyrics projection',
    text: 'Display clean, full-screen lyrics on any screen for your congregation. Music Stand only mirrors the chart to a TV for musicians. Congregation projection is a separate job there.',
  },
  {
    icon: '$music',
    title: 'One-tap transposition into any key',
    text: "Change a song from G to A on Sunday morning and every musician's chart updates in real time. No re-importing, no chart-per-key juggling.",
  },
  {
    icon: '$camera',
    title: 'Photo-to-chart OCR import',
    text: 'Snap a picture of a printed chord sheet and Song7 turns it into an editable chart. No retyping, no scanning workflow.',
  },
  {
    icon: '$formatListBulleted',
    title: 'Drag-and-drop setlist builder',
    text: "Build, reorder, and publish Sunday's setlist directly in the app. Music Stand reads setlists built in a separate Planning Center Services workflow.",
  },
  {
    icon: '$folderOutline',
    title: 'Multi-Space for multi-campus churches',
    text: 'Run separate song libraries, setlists, and teams per campus or per worship team, all under one account. No equivalent in Music Stand.',
  },
  {
    icon: '$check',
    title: 'One bill, one tool, one place to log in',
    text: 'Everything Song7 does is part of a single subscription. Music Stand is an add-on to Planning Center Services: two products, two bills, two sign-ins.',
  },
]

const matrix = [
  // Product structure
  {
    group: 'Product structure',
    feature: 'Standalone product (no other subscription required)',
    song7: true,
    musicStand: false,
  },
  { feature: 'Single bill (no parent subscription)', song7: true, musicStand: false },
  {
    feature: 'Free tier available',
    song7: `Up to ${FREE_PLAN.members} members`,
    musicStand: 'Services free tier (5 members)',
  },

  // Platforms
  { group: 'Platforms', feature: 'Runs in any browser', song7: true, musicStand: false },
  { feature: 'Windows desktop support', song7: true, musicStand: false },
  { feature: 'macOS support', song7: true, musicStand: false },
  { feature: 'iOS (iPhone)', song7: true, musicStand: true },
  { feature: 'iPad', song7: true, musicStand: true },
  { feature: 'Android phone & tablet', song7: true, musicStand: true },
  { feature: 'Native iPad app', song7: false, musicStand: true },
  { feature: 'Installable as PWA (home-screen)', song7: true, musicStand: false },

  // Song & chart management
  {
    group: 'Song & chart management',
    feature: 'Reusable song library (store & organize your own charts)',
    song7: true,
    musicStand: 'Via Services',
  },
  {
    feature: 'Chords-above-lyrics chart editor (vs inline [C]bracket syntax)',
    song7: true,
    musicStand: false,
  },
  { feature: 'ChordPro file import', song7: true, musicStand: 'Via Services' },
  { feature: 'Plain-text lyrics + chord brackets import', song7: true, musicStand: false },
  { feature: 'Photo-to-chart import (OCR)', song7: true, musicStand: false },
  { feature: 'Sheet music / PDF attachments on a song', song7: true, musicStand: true },
  { feature: 'MusicXML notation (rendered, transposable)', song7: true, musicStand: 'Not documented' },
  {
    feature: 'Direct integration with paid chart libraries (MultiTracks, SongSelect, PraiseCharts)',
    song7: false,
    musicStand: 'Via Services',
  },
  { feature: 'Built-in chord diagrams (auto-generated guitar shapes)', song7: true, musicStand: false },
  { feature: 'Export song as ChordPro', song7: true, musicStand: 'Via Services' },

  // Setlist & planning
  { group: 'Setlists & planning', feature: 'Drag-and-drop setlist builder', song7: true, musicStand: false },
  { feature: 'Per-song key in setlist', song7: true, musicStand: 'Via Services' },
  { feature: 'Set history (see what was played before)', song7: true, musicStand: false },

  // Transposition
  { group: 'Transposition', feature: 'One-tap transposition into any key', song7: true, musicStand: 'Via Services' },
  { feature: 'Real-time chart updates when key changes', song7: true, musicStand: false },

  // Live use
  { group: 'Live use & performance', feature: 'Congregation-facing lyrics projection', song7: true, musicStand: false },
  { feature: 'Chart mirroring to TV / external display', song7: true, musicStand: true },
  { feature: 'Built-in metronome', song7: true, musicStand: true },
  { feature: 'Built-in tuner', song7: true, musicStand: false },
  { feature: 'Audio reference tracks', song7: true, musicStand: true },
  { feature: 'Section looping in audio playback', song7: false, musicStand: true },
  { feature: 'Foot-pedal page turn (hardware integration)', song7: false, musicStand: true },
  { feature: 'Live page-turn sync between linked devices on stage', song7: false, musicStand: true },
  { feature: 'Free-form annotations (drawing on charts)', song7: 'Notes only', musicStand: true },
  { feature: 'Page / item reordering on the fly', song7: 'Setlist reorder', musicStand: true },
  { feature: 'Dark mode for stage / low-light use', song7: true, musicStand: 'Likely (not documented)' },
  { feature: 'Native offline mode on stage', song7: 'PWA cache', musicStand: true },

  // Team
  {
    group: 'Team & collaboration',
    feature: 'Real-time team sync of plan and chart changes',
    song7: true,
    musicStand: false,
  },
  { feature: 'Multi-Space (multiple campuses / teams)', song7: true, musicStand: false },
  { feature: 'Web-based admin panel', song7: true, musicStand: 'Via Services web' },
  { feature: 'Notes on songs', song7: 'Shared with the team', musicStand: true },
]

const faqItems = [
  {
    question: 'Song7 vs Planning Center Music Stand: which should I choose?',
    answer:
      "It comes down to standalone versus add-on. Music Stand is a stage-reading add-on that requires an active Planning Center Services subscription. Charts only reach it after they're in Services first, and it doesn't run on Windows. Song7 is a single standalone product that covers song management, setlist building, transposition, team sync, and live presentation without a separate scheduling subscription, and it runs in any browser including Windows. Choose Music Stand if you're committed to the Planning Center ecosystem and rely on its foot-pedal and annotation depth; choose Song7 if you want one self-contained cross-platform tool.",
  },
  {
    question: 'Is Music Stand by Planning Center free?',
    answer:
      'No. Music Stand is sold as an add-on to Planning Center Services, priced at $5/month (Services plan with 50 team members) or $10/month (Services plan with 150 team members). It requires an active Services subscription, which starts at $15/month for 20 team members. Services does offer a free tier capped at 5 team members and 100MB of storage; whether the Music Stand add-on is included on the free Services tier is best confirmed directly on planningcenter.com.',
  },
  {
    question: 'Do I need Planning Center Services to use Music Stand?',
    answer:
      'Yes. Planning Center states explicitly that "Music Stand is an add-on app to Services, so you need a Services subscription to use it." Music charts only appear in Music Stand after they\'ve been added to a Services account first.',
  },
  {
    question: "What's the best alternative to Music Stand by Planning Center?",
    answer:
      'Song7 is the strongest alternative if you want a standalone tool that replaces both Music Stand and Planning Center Services in one product. Other alternatives include OnSong (iPad-first chord chart app) and ProPresenter (presentation-first software), but neither is a like-for-like replacement of the full Planning Center workflow the way Song7 is.',
  },
  {
    question: 'Can Song7 do everything Music Stand does?',
    answer:
      "Mostly, but with a different shape. Song7 covers song management, setlist building, transposition, team sync, and live presentation in one tool. Music Stand has a narrower but more mature feature set in two specific areas: hardware foot-pedal page turns and heavy free-form chart annotations. If those two are deal-breakers, Music Stand still wins. For most worship teams' day-to-day workflow, Song7 covers everything important.",
  },
  {
    question: 'Does Song7 work on iPad?',
    answer:
      'Yes. Song7 runs in any modern browser on iPad and installs as a Progressive Web App from Safari for a fullscreen, home-screen-launchable experience. There is no separate native iPad app required.',
  },
  {
    question: 'Does Song7 work on Windows?',
    answer:
      "Yes. Song7 runs in any browser on Windows. This is a meaningful difference from Music Stand, which Planning Center explicitly states is not compatible with Windows devices. If anyone on your team uses a Windows laptop on stage, that's a hard blocker for Music Stand but works fine in Song7.",
  },
  {
    question: 'Can I import my songs from Planning Center into Song7?',
    answer:
      "Yes. Song7 accepts ChordPro files, plain text with chord brackets, and JSON imports, all formats you can produce from a Planning Center Services chart. ChordPro preserves chord positions and parses cleanly. For songs you can only get as a printed sheet or image, Song7's photo-to-chart capture works as a fallback. Song7 does not currently import PDFs directly.",
  },
  {
    question: 'Does Song7 support foot-pedal page turns?',
    answer:
      'Not as a first-class, supported feature. Song7 does not currently ship a curated foot-pedal experience the way Music Stand does (Music Stand officially recommends the AirTurn DUO 500 and BT500S-2). If hands-free hardware pedal page turns are critical, Music Stand has the edge today.',
  },
  {
    question: 'Is Song7 cheaper than Planning Center?',
    answer:
      "It depends on whether you'd be running Services anyway. If your church already uses Planning Center Services for scheduling, adding Music Stand is cheap incremental cost ($5–10/month). If you're starting from zero and need both song management and a chord chart display, Song7's single subscription is usually meaningfully cheaper than Services + Music Stand combined. See song7.io/pricing for current Song7 numbers.",
  },
  {
    question: 'Is Music Stand available on Android?',
    answer:
      'Yes. Planning Center states Music Stand runs on any iOS or Android phone or tablet. Windows devices are not supported.',
  },
  {
    question: 'Can I use Song7 without internet during a service?',
    answer:
      "Song7 caches content for offline use as a Progressive Web App, which covers most short connectivity drops. For services where rock-solid native offline mode is non-negotiable, Music Stand's native mobile caching is more battle-tested than a PWA approach today.",
  },
  {
    question: 'How does Song7 compare to Planning Center for small churches?',
    answer:
      "Very small churches (5 or fewer active volunteers) can use Planning Center Services on its free tier, which is hard to beat on price, though Song7's free plan covers teams of up to 10. Above that threshold, Song7's single subscription tends to be both simpler and cheaper than Services plus the Music Stand add-on. Beyond pricing, Song7's appeal for small churches is that it's one tool: less to learn, less to onboard volunteers into, less to maintain.",
  },
  {
    question: 'Does Song7 integrate with Planning Center Services?',
    answer:
      'Not directly. Song7 is designed as a standalone replacement for the worship music workflow, not as a companion to Planning Center. If you need to coexist with Services, the practical path is to export from Services and import into Song7, then run Song7 as your worship tool while keeping Services for general church scheduling if you still want it for that.',
  },
]

useSeo({
  keywords: [
    'music stand by planning center alternative',
    'song7 vs music stand',
    'planning center music stand review',
    'planning center alternative for worship',
    'chord chart app for churches',
    'best music stand alternative',
    'worship music app comparison',
  ],
  schema: 'SoftwareApplication',
  faq: faqItems,
})
</script>

<style scoped>
.compare-hero {
  background:
    radial-gradient(1000px 600px at 80% 0%, rgba(var(--v-theme-primary), 0.12), transparent 60%),
    radial-gradient(800px 500px at 0% 30%, rgba(var(--v-theme-secondary), 0.08), transparent 60%);
}

.compare-h1 {
  font-size: clamp(34px, 5vw, 56px);
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.1;
}

.compare-quick-facts {
  list-style: none;
  padding: 0;
  margin: 0;
}

.compare-quick-facts li {
  padding: 4px 0;
  font-size: 0.95rem;
}

.verdict-list,
.migration-steps {
  padding-left: 1.25rem;
  margin: 0;
}

.verdict-list li,
.migration-steps li {
  margin-bottom: 0.6rem;
  line-height: 1.55;
}

.compare-table th {
  font-weight: 700 !important;
  font-size: 0.95rem;
}

.deep-dive {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  padding: 1.75rem 0;
}

.deep-dive:last-child {
  border-bottom: none;
}

.deep-dive p {
  line-height: 1.65;
}

.price-breakdown {
  list-style: none;
  padding: 0;
  margin: 0;
}

.price-breakdown li {
  padding: 8px 0;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  font-size: 0.95rem;
}

.price-breakdown li:last-child {
  border-bottom: none;
}

.pulse-button {
  animation: pulseButton 2s infinite;
}

.switcher-callout {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background: rgba(var(--v-theme-success), 0.1);
  color: rgb(var(--v-theme-success));
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s ease;
}

.switcher-callout:hover {
  background: rgba(var(--v-theme-success), 0.18);
}

.compare-table-group th {
  background: rgba(var(--v-theme-on-surface), 0.04);
  color: rgba(var(--v-theme-on-surface), 0.6);
  letter-spacing: 0.06em;
  padding-top: 16px !important;
  padding-bottom: 8px !important;
}

.showcase-card {
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.showcase-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
}

.showcase-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-primary), 0.12);
  color: rgb(var(--v-theme-primary));
}

@keyframes pulseButton {
  0% {
    box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(var(--v-theme-primary), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0);
  }
}
</style>
